import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { Zap, Flame, Droplets, Send, CheckCircle, Home, Phone, User, HelpCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const translations = {
  ro: {
    title: "Transmitere Index",
    subtitle: "Trimiteți indexurile contoarelor pentru apartamentul dvs.",
    selectApartment: "Selectați Apartamentul",
    electricIndex: "Index Curent Electric (kWh)",
    gasIndex: "Index Gaz (m³)",
    waterIndex: "Index Apă (m³)",
    name: "Nume",
    phone: "Telefon",
    submit: "Trimite Index",
    success: "Index trimis cu succes!",
    successDesc: "Mulțumim pentru transmiterea indexurilor.",
    required: "Selectați un apartament",
    optional: "opțional",
    example: "Exemplu"
  },
  en: {
    title: "Submit Meter Readings",
    subtitle: "Submit your utility meter readings for your apartment.",
    selectApartment: "Select Apartment",
    electricIndex: "Electric Meter Reading (kWh)",
    gasIndex: "Gas Meter Reading (m³)",
    waterIndex: "Water Meter Reading (m³)",
    name: "Name",
    phone: "Phone",
    submit: "Submit Reading",
    success: "Reading submitted successfully!",
    successDesc: "Thank you for submitting your meter readings.",
    required: "Please select an apartment",
    optional: "optional",
    example: "Example"
  },
  fr: {
    title: "Relevé des Compteurs",
    subtitle: "Soumettez vos relevés de compteurs pour votre appartement.",
    selectApartment: "Sélectionner l'Appartement",
    electricIndex: "Relevé Électrique (kWh)",
    gasIndex: "Relevé Gaz (m³)",
    waterIndex: "Relevé Eau (m³)",
    name: "Nom",
    phone: "Téléphone",
    submit: "Soumettre",
    success: "Relevé soumis avec succès!",
    successDesc: "Merci d'avoir soumis vos relevés.",
    required: "Veuillez sélectionner un appartement",
    optional: "optionnel",
    example: "Exemple"
  }
};

export const UtilityIndexPage = () => {
  const { language } = useApp();
  const t = translations[language] || translations.ro;
  
  const [apartments, setApartments] = useState([]);
  const [settings, setSettings] = useState({});
  const [selectedApartment, setSelectedApartment] = useState('');
  const [electricIndex, setElectricIndex] = useState('');
  const [gasIndex, setGasIndex] = useState('');
  const [waterIndex, setWaterIndex] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showExample, setShowExample] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [aptRes, settingsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/apartments`),
        axios.get(`${BACKEND_URL}/api/settings/global`)
      ]);
      setApartments(aptRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedApartment) {
      toast.error(t.required);
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/utility-index`, {
        apartment_id: selectedApartment,
        electric_index: electricIndex ? parseFloat(electricIndex) : null,
        gas_index: gasIndex ? parseFloat(gasIndex) : null,
        water_index: waterIndex ? parseFloat(waterIndex) : null,
        name: name,
        phone: phone
      });
      
      setSubmitted(true);
      toast.success(t.success);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedApartment('');
    setElectricIndex('');
    setGasIndex('');
    setWaterIndex('');
    setName('');
    setPhone('');
    setSubmitted(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-32 pb-20 px-4">
        <div className="max-w-xl mx-auto">
          
          {submitted ? (
            <div className="glass p-8 text-center">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-green-400" size={40} />
              </div>
              <h1 className="text-3xl font-serif mb-4">{t.success}</h1>
              <p className="text-muted-foreground mb-8">{t.successDesc}</p>
              <Button onClick={resetForm} variant="outline">
                {t.title}
              </Button>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h1 className="text-4xl font-serif mb-4">{t.title}</h1>
                <p className="text-muted-foreground">{t.subtitle}</p>
              </div>
              
              <form onSubmit={handleSubmit} className="glass p-6 space-y-6">
                {/* Apartment Selection */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium mb-2">
                    <Home size={16} className="text-primary" />
                    {t.selectApartment} *
                  </label>
                  <select
                    value={selectedApartment}
                    onChange={(e) => setSelectedApartment(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-900 border border-white/10 rounded focus:border-primary focus:outline-none"
                    required
                  >
                    <option value="">-- {t.selectApartment} --</option>
                    {apartments.map((apt) => (
                      <option key={apt.id} value={apt.id}>
                        {apt.title?.[language] || apt.title?.ro || apt.title?.en || 'Apartment'}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Electric Index */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <Zap size={16} className="text-yellow-400" />
                      {t.electricIndex}
                    </label>
                    {settings.utility_electric_image && (
                      <button
                        type="button"
                        onClick={() => setShowExample(showExample === 'electric' ? null : 'electric')}
                        className="flex items-center gap-1 text-xs text-yellow-400 hover:text-yellow-300"
                      >
                        <HelpCircle size={14} />
                        {t.example}
                      </button>
                    )}
                  </div>
                  {showExample === 'electric' && settings.utility_electric_image && (
                    <div className="mb-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <img 
                        src={settings.utility_electric_image} 
                        alt="Electric meter example" 
                        className="max-h-40 mx-auto rounded"
                      />
                      <p className="text-xs text-center text-yellow-400 mt-2">{t.example}</p>
                    </div>
                  )}
                  <input
                    type="number"
                    step="0.01"
                    value={electricIndex}
                    onChange={(e) => setElectricIndex(e.target.value)}
                    placeholder={settings.utility_electric_placeholder || "ex: 12345.67"}
                    className="w-full px-4 py-3 bg-slate-900 border border-white/10 rounded focus:border-yellow-400 focus:outline-none"
                  />
                </div>

                {/* Gas Index */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <Flame size={16} className="text-orange-400" />
                      {t.gasIndex}
                    </label>
                    {settings.utility_gas_image && (
                      <button
                        type="button"
                        onClick={() => setShowExample(showExample === 'gas' ? null : 'gas')}
                        className="flex items-center gap-1 text-xs text-orange-400 hover:text-orange-300"
                      >
                        <HelpCircle size={14} />
                        {t.example}
                      </button>
                    )}
                  </div>
                  {showExample === 'gas' && settings.utility_gas_image && (
                    <div className="mb-3 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                      <img 
                        src={settings.utility_gas_image} 
                        alt="Gas meter example" 
                        className="max-h-40 mx-auto rounded"
                      />
                      <p className="text-xs text-center text-orange-400 mt-2">{t.example}</p>
                    </div>
                  )}
                  <input
                    type="number"
                    step="0.01"
                    value={gasIndex}
                    onChange={(e) => setGasIndex(e.target.value)}
                    placeholder={settings.utility_gas_placeholder || "ex: 1234.56"}
                    className="w-full px-4 py-3 bg-slate-900 border border-white/10 rounded focus:border-orange-400 focus:outline-none"
                  />
                </div>

                {/* Water Index */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <Droplets size={16} className="text-blue-400" />
                      {t.waterIndex}
                    </label>
                    {settings.utility_water_image && (
                      <button
                        type="button"
                        onClick={() => setShowExample(showExample === 'water' ? null : 'water')}
                        className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                      >
                        <HelpCircle size={14} />
                        {t.example}
                      </button>
                    )}
                  </div>
                  {showExample === 'water' && settings.utility_water_image && (
                    <div className="mb-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <img 
                        src={settings.utility_water_image} 
                        alt="Water meter example" 
                        className="max-h-40 mx-auto rounded"
                      />
                      <p className="text-xs text-center text-blue-400 mt-2">{t.example}</p>
                    </div>
                  )}
                  <input
                    type="number"
                    step="0.01"
                    value={waterIndex}
                    onChange={(e) => setWaterIndex(e.target.value)}
                    placeholder={settings.utility_water_placeholder || "ex: 123.45"}
                    className="w-full px-4 py-3 bg-slate-900 border border-white/10 rounded focus:border-blue-400 focus:outline-none"
                  />
                </div>

                {/* Name */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium mb-2">
                    <User size={16} className="text-primary" />
                    {t.name} <span className="text-muted-foreground text-xs">({t.optional})</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-900 border border-white/10 rounded focus:border-primary focus:outline-none"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium mb-2">
                    <Phone size={16} className="text-primary" />
                    {t.phone} <span className="text-muted-foreground text-xs">({t.optional})</span>
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-900 border border-white/10 rounded focus:border-primary focus:outline-none"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full py-6 text-lg"
                  disabled={loading}
                >
                  {loading ? (
                    'Se trimite...'
                  ) : (
                    <>
                      <Send size={20} className="mr-2" />
                      {t.submit}
                    </>
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default UtilityIndexPage;
